<template>
  <main v-if="card" class="padding-top-none">
    <al-modal
      :heading="$t('provisionCreate.forgetCardHeading')"
      :close-label="$t('provisionCreate.forgetCardCancel')"
      name="confirm-forget-card-modal">
      <div class="row align-left">
        <div class="col-full">
          <al-card-preview
            :brand="card.shortProductDescription || card.issuerName || card.paymentNetworkName"
            :img="card.artUrl"
            :number="`•••• ${card.primaryAccountNumberSuffix}`"
            name="forget-card-confirm-preview" />
        </div>
      </div>
      <template slot="footer">
        <div class="row row-vertical-align-bottom">
          <div class="col-full">
            <al-button
              :loading="loading"
              :label="$t('provisionCreate.forgetCardConfirm')"
              uppercase
              name="button-confirm-forget"
              @click="handleForget" />
          </div>
        </div>
      </template>
    </al-modal>
    <tux-provision-create
      v-model="form"
      :card="card"
      :countries="countries"
      :country-code="consumer.countryCode"
      :existing-user="isExistingUser"
      :national-id-error="nationalIdError"
      :manual-error="errorToken"
      :privacy-url="privacyUrl"
      :terms-url="termsUrl"
      :google-privacy-url="googlePrivacyUrl"
      :google-terms-url="googleTermsUrl"
      :enable-autocomplete="shouldEnableAutocomplete"
      allow-partner-banner
      editable-billing-address
      editable-consumer-info
      show-marketing-opt-in
      @autocomplete-selected="handleAutocompleteSelected"
      @edit-billing-address="handleEditBillingAddress"
      @privacy-click="handlePrivacyClick"
      @save-as-shipping="handleSaveAsShipping"
      @submit="handleSubmit"
      @terms-click="handleTermsClick"
      @input="clearErrors">
      <template slot="secondary-cta">
        <button
          v-analytics:click="$EVENTS.FORGET_CARD_BUTTON"
          v-toggle:al-modal="'confirm-forget-card-modal'"
          :aria-label="$t('provisionCreate.forgetCard')"
          type="button"
          class="btn--secondary"
          data-test="secondary-cta">
          {{ $t('provisionCreate.forgetCard') }}
        </button>
      </template>
    </tux-provision-create>
  </main>
</template>

<script>
import { AlButton, AlCardPreview, AlModal } from '@mc-alberta/alcapone'
import nudetect from '@mc-alberta/nudetect'
import { joinAddressNames, splitAddressNames } from '@mc-alberta/supernova-scripts'
import { TuxProvisionCreate } from '@mc-alberta/tuxedo'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import CONSTANTS from '@/constants'
import { transformEmvcoToMastercard } from '@/store/modules/cards'
import * as types from '@/store/mutation-types'

export default {
  name: 'ReviewProvision',
  components: { AlButton, AlCardPreview, AlModal, TuxProvisionCreate },
  data() {
    return {
      autocompleteSelected: false,
      card: null,
      editBillingClicked: false,
      errorToken: null,
      loading: false,
      form: {
        billingAddress: {},
        nationalId: '',
        phoneNumber: {
          country: '',
          countryCode: '',
          phoneNumber: ''
        },
        marketingOptIn: false,
        saveAsShipping: true,
        shippingAddress: {},
        userId: ''
      },
      initialPhoneNumber: null,
      nationalIdError: null,
      storedPayload: null,
      googlePrivacyUrl: process.env.GOOGLE_PRIVACY,
      googleTermsUrl: process.env.GOOGLE_TERMS
    }
  },
  computed: {
    ...mapGetters([
      'countries',
      'privacyUrl',
      'serviceErrorResponse',
      'termsUrl',
      'newCardAddressFormat',
      'complianceTermsUrl',
      'compliancePrivacyUrl'
    ]),
    ...mapState({
      consumer: (state) => state.user.consumer,
      interceptUnauthorizedError: (state) => state.interceptUnauthorizedError,
      isExistingUser: (state) => state.cards.provisioningData.status === CONSTANTS.BULK.PROVISIONED,
      newCard: (state) => state.cards.newCard,
      provisioningData: (state) => state.cards.provisioningData,
      registrationData: (state) => state.registrationData,
      unprovisionedCards: (state) => state.cards.unprovisionedCards
    }),
    countryISO() {
      return this.$i18nData.getPhoneCountryISO(
        this.consumer.phone?.countryCode,
        this.consumer.countryCode
      )
    },
    editedPhoneNumber() {
      const { country: initialCountry, phoneNumber: initialPhoneNumber } = this.initialPhoneNumber
      const { country, phoneNumber } = this.form.phoneNumber

      return initialCountry !== country || initialPhoneNumber !== phoneNumber
    },
    hasShippingName() {
      return this.form.shippingAddress?.name || this.form.shippingAddress?.firstName
    },
    shouldEnableAutocomplete() {
      return !this.$switchboard.isTestOverrideEnabled(CONSTANTS.TEST_OVERRIDES.GOOGLE_AUTOCOMPLETE)
    },
    shouldSubmitBillingAddress() {
      return Object.keys(this.form.billingAddress).some((addressKey) => {
        return addressKey !== 'countryCode' && !!this.form.billingAddress[addressKey]
      })
    },
    nationalIdConfig() {
      const { consumer } = this.$i18nData.getCountryConfig(this.consumer.countryCode) || {}
      return consumer?.nationalId
    },
    providedBillingAddressFormat() {
      const providedAddress = this.provisioningData.billingAddress || {}

      if (providedAddress.line1) {
        return CONSTANTS.ADDRESS_FORMAT.FULL
      }

      if (providedAddress.zip) {
        return CONSTANTS.ADDRESS_FORMAT.POSTAL_CODE
      }

      return CONSTANTS.ADDRESS_FORMAT.NONE
    }
  },
  created() {
    const card = { ...this.newCard }
    const { billingAddress } = this.provisioningData

    // Re-route back to unprovisioned list if there is no card data (i.e. refreshing the page)
    if (!card || !Object.keys(card).length) {
      return this.$router.replace({ name: CONSTANTS.ROUTES.LIST_CARDS_UNPROVISIONED })
    }

    this.card = transformEmvcoToMastercard(card)
    this.form.billingAddress = joinAddressNames({
      ...billingAddress,
      countryCode: billingAddress?.countryCode || this.consumer.countryCode
    })

    if (this.registrationData?.consumer?.mobileNumber) {
      this.form.phoneNumber = this.registrationData.consumer.mobileNumber
    } else {
      this.form.phoneNumber.country = this.countryISO
      this.form.phoneNumber.countryCode = this.consumer.phone?.countryCode
      this.form.phoneNumber.phoneNumber = this.consumer.phone?.mobileNumber
    }

    this.form.shippingAddress.countryCode = this.consumer.countryCode
    this.form.userId = this.consumer.email

    this.initialPhoneNumber = this.form.phoneNumber
  },
  mounted() {
    this.$nextTick(() => nudetect.beginBehaviouralMonitoring())
    this.$validator.validateAll()
  },
  beforeDestroy() {
    this.clearServiceLayerError()
    this.$validator.pause()
  },
  methods: {
    ...mapActions([
      'bindAdditionalUserData',
      'clearServiceLayerError',
      'stopLoader',
      'stopLoaderWithSuccess',
      'fetchVerificationId',
      'loadCustomerProfile',
      'registerUser',
      'startLoader',
      'forgetUnprovisionedCard',
      'clearNewCardData',
      'setRegistrationData'
    ]),
    ...mapMutations({
      setInterceptUnauthorized: types.SET_INTERCEPT_UNAUTHORIZED_ERROR
    }),
    async handleForget() {
      this.clearServiceLayerError()
      this.loading = true

      await this.forgetCardActions()

      if (this.serviceErrorResponse()) {
        this.$router.replace({ name: CONSTANTS.ROUTES.ERROR, params: { logout: true } })
        return this.stopLoader(CONSTANTS.LOADERS.ADD_PAYMENT)
      }

      this.loading = false
      this.postUpdateActions({ showBanner: false })
    },
    handleSubmit() {
      const useBillingAsShipping = this.form.saveAsShipping && this.form.billingAddress.zip
      const hasShippingData = !!(useBillingAsShipping || this.hasShippingName)

      this.$analytics.track({
        event: this.$EVENTS.CONTINUE_BUTTON,
        meta: {
          autocompleteSelected: this.autocompleteSelected,
          editBilling: this.editBillingClicked,
          editPhoneNumber: this.editedPhoneNumber,
          marketingOptIn: this.form.marketingOptIn,
          shippingSameAsBilling: this.form.saveAsShipping,
          hasBillingData: this.providedBillingAddressFormat,
          hasShippingData,
          hasNationalId: !!this.nationalIdConfig,
          marketBillingPreference: this.newCardAddressFormat,
          consumerNameRequested: false
        }
      })

      this.storedPayload = null
      this.handleBind()
    },
    async handleBind() {
      this.clearServiceLayerError()
      this.startLoader(CONSTANTS.LOADERS.ADD_PAYMENT)

      if (this.form.saveAsShipping) {
        this.form.shippingAddress = { ...this.form.billingAddress }
      }

      this.isExistingUser ? await this.existingUserActions() : await this.newUserActions()

      nudetect.clearBehaviouralData()

      if (this.serviceErrorResponse()) {
        return this.handleError()
      }

      await this.postUpdateActions()

      this.stopLoaderWithSuccess(CONSTANTS.LOADERS.ADD_PAYMENT)
    },
    async existingUserActions() {
      const previousInterceptValue = this.interceptUnauthorizedError
      const payload = this.buildAddBillingPayload()

      // Intercept so 401 routes to OTP instead of session expired flow
      this.setInterceptUnauthorized(false)
      await this.bindAdditionalUserData(payload)

      this.setInterceptUnauthorized(previousInterceptValue)
    },
    async newUserActions() {
      const payload = this.buildRegisterUserPayload()
      this.setRegistrationData({
        consumer: {
          emailAddress: payload.consumer.emailAddress,
          mobileNumber: payload.consumer.mobileNumber
        }
      })
      await this.registerUser(payload)
    },
    async forgetCardActions() {
      this.setRegistrationData({
        consumer: {
          ...this.registrationData?.consumer,
          emailAddress: this.consumer.email
        }
      })

      await this.forgetUnprovisionedCard(this.card.id)
    },
    async postUpdateActions({ showBanner = true } = {}) {
      // Load customer profile again to check if there are still unprovisioned cards remaining
      await this.loadCustomerProfile()

      if (this.serviceErrorResponse()) {
        const { status } = this.serviceErrorResponse()
        if (status === 401 && this.unprovisionedCards.length === 1) {
          this.$router.replace({ name: CONSTANTS.ROUTES.ADD_CARD, params: { logout: true } })
        } else {
          this.$router.replace({ name: CONSTANTS.ROUTES.ERROR, params: { logout: true } })
        }

        return this.stopLoader(CONSTANTS.LOADERS.ADD_PAYMENT)
      }

      if (this.unprovisionedCards.length > 0) {
        this.$router.replace({
          name: CONSTANTS.ROUTES.LIST_CARDS_UNPROVISIONED,
          params: {
            isNewUser: !this.isExistingUser,
            showBanner
          }
        })
      } else {
        this.$router.replace({
          name: CONSTANTS.ROUTES.PROFILE_HOME,
          params: {
            isNewUser: !this.isExistingUser,
            addedNewCard: this.isExistingUser
          }
        })
      }

      this.clearNewCardData()
    },
    buildAddBillingPayload() {
      if (this.storedPayload) {
        return this.storedPayload
      }

      const payload = {
        ...(this.shouldSubmitBillingAddress && { billingAddress: this.form.billingAddress }),
        ...(this.hasShippingName && { shippingAddress: this.form.shippingAddress })
      }

      this.storedPayload = payload
      return payload
    },
    buildRegisterUserPayload() {
      if (this.storedPayload) {
        return this.storedPayload
      }

      const { firstName, lastName } = splitAddressNames(this.form.billingAddress)
      const { email: emailAddress, languageCode, countryCode } = this.consumer

      const consumer = {
        emailAddress,
        mobileNumber: this.form.phoneNumber,
        consumerIdentity: this.consumerIdentity,
        complianceSettings: {
          privacyAcceptedVersion: 'latest',
          privacyLatestVersionUri: this.compliancePrivacyUrl,
          tncAcceptedVersion: 'latest',
          tncLatestVersionUri: this.complianceTermsUrl,
          cookieAcceptedVersion: '1.0',
          marketingOptIn: this.form.marketingOptIn
        },
        countryCode,
        languageCode,
        firstName,
        lastName
      }

      if (this.nationalIdConfig) {
        consumer.nationalIdentifier = this.form.nationalId
      }

      const payload = {
        ...(this.shouldSubmitBillingAddress && { billingAddress: this.form.billingAddress }),
        consumer,
        ...(this.hasShippingName && { shippingAddress: this.form.shippingAddress })
      }

      this.storedPayload = payload
      return payload
    },
    handleError() {
      const { status, data } = this.serviceErrorResponse()
      const isSourceNationalId = data.details?.some(({ source }) => {
        return source === CONSTANTS.ERROR_SOURCE.NATIONAL_ID
      })
      const isSourceBilling = data.details?.some(({ source }) => {
        return source === CONSTANTS.ERROR_SOURCE.BILLING_ADDRESS
      })

      if (status === 400 && isSourceNationalId) {
        this.nationalIdError = 'validation.messages.national_id'
      } else if (status === 400 && isSourceBilling) {
        nudetect.beginBehaviouralMonitoring()
        this.errorToken = 'error.billing'
      } else if (status === 400) {
        nudetect.beginBehaviouralMonitoring()
        this.errorToken = 'error.default'
      } else if (status === 401) {
        return this.handleUnauthorizedUser()
      } else {
        this.$router.replace({ name: CONSTANTS.ROUTES.ERROR })
      }

      this.stopLoader(CONSTANTS.LOADERS.ADD_PAYMENT)
    },
    async handleUnauthorizedUser() {
      nudetect.beginBehaviouralMonitoring()

      this.clearServiceLayerError()
      await this.fetchVerificationId(CONSTANTS.VALIDATION_CHANNEL.SMS)

      if (this.serviceErrorResponse()) {
        return this.handleError()
      }

      this.$router.push({
        name: CONSTANTS.ROUTES.VERIFY_ACCOUNT,
        params: {
          userFound: this.isExistingUser,
          onSuccess: this.handleBind
        }
      })

      this.stopLoader(CONSTANTS.LOADERS.ADD_PAYMENT)
    },
    handleAutocompleteSelected() {
      this.autocompleteSelected = true
    },
    handleEditBillingAddress() {
      this.editBillingClicked = true
    },
    handlePrivacyClick() {
      this.$analytics.track(this.$EVENTS.COPYRIGHT_BUTTON)
    },
    handleSaveAsShipping() {
      this.$analytics.track(this.$EVENTS.SHIP_TO_BILLING_ADDRESS)
    },
    handleTermsClick() {
      this.$analytics.track(this.$EVENTS.TERMS_BUTTON)
    },
    clearErrors() {
      this.errorToken = null
      this.nationalIdError = null
    }
  }
}
</script>
